import { useEffect, useState } from 'react';
import type { Observable } from 'rxjs';
import { shouldRenderTable, renderTableLayout } from './renderTableLayout';
import type { TableLayoutShape, TableSettings } from './contracts';

interface DynamicTableRendererProps {
    shape$: Observable<TableLayoutShape>;
    settings: TableSettings;
    keepEmptyVisible: boolean;
}

export function DynamicTableRenderer({
    shape$,
    settings,
    keepEmptyVisible,
}: DynamicTableRendererProps): JSX.Element | null {
    const [renderedShape, setRenderedShape] = useState<TableLayoutShape | null>(null);
    const [renderError, setRenderError] = useState<Error | null>(null);

    useEffect(() => {
        let isActive = true;
        setRenderedShape(null);
        setRenderError(null);

        const subscription = shape$.subscribe({
            next(nextShape) {
                if (!isActive) {
                    return;
                }

                setRenderedShape(nextShape);
            },
            error(error) {
                if (!isActive) {
                    return;
                }

                setRenderError(error instanceof Error ? error : new Error(String(error)));
            },
        });

        return () => {
            isActive = false;
            subscription.unsubscribe();
        };
    }, [shape$]);

    if (renderError) {
        throw renderError;
    }

    if (!renderedShape) {
        return keepEmptyVisible ? renderTableLayout(settings, settings) : null;
    }

    return shouldRenderTable(settings, renderedShape, keepEmptyVisible)
        ? renderTableLayout(settings, renderedShape)
        : null;
}
